import { useRef, type ReactNode } from 'react';
import { signalTick } from '../pos/feedback';
import { SWIPE_IDLE, swipeEnd, swipeMove, swipeStart, type SwipeState } from '../pos/swipe';

/**
 * Listenzeile, die man nach links wegwischen kann (K2). Die Geste ist nie der einzige Weg: Wer nicht wischen kann, nutzt
 * den Knopf „Entfernen“ in der Zeile (Kinder). Elemente mit `data-noswipe` starten keine Geste. Die Zeile bewegt sich
 * über `transform` direkt am Element (kein Neuzeichnen je Bewegung). `touch-action: pan-y` lässt das senkrechte
 * Scrollen dem Browser. Entscheidungen trifft die reine Zustandsmaschine in `pos/swipe.ts`.
 */
export function SwipeRow({
  children,
  onRemove,
  onTap,
  disabled = false,
  hint = 'Entfernen',
  className,
}: {
  children: ReactNode;
  /** Wird nach dem Wegwischen aufgerufen. */
  onRemove: () => void;
  /** Antippen der Zeile (ohne Wischen). */
  onTap?: () => void;
  disabled?: boolean;
  hint?: string;
  className?: string;
}) {
  const row = useRef<HTMLLIElement>(null);
  const front = useRef<HTMLDivElement>(null);
  const state = useRef<SwipeState>(SWIPE_IDLE);
  const armed = useRef(false);
  const dragged = useRef(false);

  const show = (offset: number, isArmed: boolean): void => {
    if (front.current !== null) {
      front.current.style.transform = offset === 0 ? '' : `translateX(${String(offset)}px)`;
    }
    if (row.current !== null)
      row.current.dataset.state = offset === 0 ? '' : isArmed ? 'armed' : 'drag';
  };
  const reset = (): void => {
    state.current = SWIPE_IDLE;
    armed.current = false;
    show(0, false);
  };

  return (
    <li ref={row} className={`swipe ${className ?? ''}`}>
      <div className="swipe__bg" aria-hidden="true">
        <span className="swipe__hint">{hint}</span>
        <span className="swipe__hint swipe__hint--armed">Loslassen zum Entfernen</span>
      </div>
      <div
        ref={front}
        className="swipe__front"
        onPointerDown={(down) => {
          dragged.current = false;
          if (disabled || !down.isPrimary) return;
          if (down.pointerType === 'mouse' && down.button !== 0) return;
          if (down.target instanceof Element && down.target.closest('[data-noswipe]') !== null) {
            return;
          }
          state.current = swipeStart(down.clientX, down.clientY, down.timeStamp, window.innerWidth);
        }}
        onPointerMove={(move) => {
          if (state.current.phase === 'idle') return;
          const before = state.current.phase;
          const width = row.current?.getBoundingClientRect().width ?? 0;
          state.current = swipeMove(state.current, move.clientX, move.clientY, width);
          const { phase, offset } = state.current;
          if (phase !== 'dragging' && phase !== 'armed') return;
          if (before === 'pending') {
            dragged.current = true;
            try {
              move.currentTarget.setPointerCapture(move.pointerId);
            } catch {
              // ohne Erfassung bleibt die Geste innerhalb der Zeile nutzbar
            }
          }
          if ((phase === 'armed') !== armed.current) {
            armed.current = phase === 'armed';
            if (armed.current) signalTick();
          }
          show(offset, armed.current);
        }}
        onPointerUp={(up) => {
          const width = row.current?.getBoundingClientRect().width ?? 0;
          const end = swipeEnd(state.current, up.clientX, up.clientY, up.timeStamp, width);
          reset();
          if (end.phase === 'committed' && !disabled) {
            signalTick();
            onRemove();
          }
        }}
        onPointerCancel={reset}
        onClick={(click) => {
          // Ein Wischen endet in einem Klick auf demselben Element: der zählt nicht als Antippen
          if (dragged.current) {
            dragged.current = false;
            click.stopPropagation();
            return;
          }
          if (
            onTap !== undefined &&
            !(click.target instanceof Element && click.target.closest('[data-noswipe]'))
          ) {
            onTap();
          }
        }}
      >
        {children}
      </div>
    </li>
  );
}
